import { DataFileInterface } from '@systemeio/file-manager'
import { BadRequest } from '@systemeio/util'
import { DomainId } from 'modules/profile/mailing-settings/types/mailing-settings-domain-interface'
import { useEffect, useState } from 'react'
import { useActOnBlur } from 'shared/hooks/use-act-on-blur'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useModal } from 'shared/hooks/use-modal'
import { useCourse } from '../../../hooks/use-course'
import { CourseEditApiInterface, CourseId } from '../../../types/course-interface'
import { useLightCourseActions } from '../../hooks/use-light-course-actions'

export const useSaveOnBlur = ({ courseId }: { courseId?: CourseId }) => {
  const { t } = useLocoTranslation()
  const [tempState, setTempState] = useState<Partial<CourseEditApiInterface>>()
  const { courseData, mutate } = useCourse(courseId)
  const { patchLightCourseOptions } = useLightCourseActions()
  const domainConfirmationModal = useModal<DomainId>()
  const [isCodeValid, setIsCodeValid] = useState(true)

  useEffect(() => {
    if (courseData) {
      setTempState({
        ...courseData,
      })
    } else {
      setTempState(undefined)
    }
  }, [courseData?.id])

  const instructorNamePatcher = useActOnBlur<string>(state => ({
    action: async value => {
      if (!courseId) throw new Error()
      const { instructorName } = await patchLightCourseOptions(
        courseId,
        { instructorName: value || null },
        'patchInstructorName',
      )
      await mutate(data => {
        if (!data) return
        return {
          ...data,
          instructorName,
        }
      }, false)
    },
    onError: e => {
      if (e instanceof BadRequest) {
        state.setStatus(e.errors.fields.instructorName.toString() || t('global.error'))
      } else {
        state.setStatus(t('global.error'))
      }
    },
  }))

  const instructorPhotoPatcher = useActOnBlur<number | null>(state => ({
    action: async value => {
      if (!courseId) throw new Error()
      const { teacherPhoto } = await patchLightCourseOptions(
        courseId,
        { teacherPhoto: value },
        'patchTeacherPhoto',
      )
      await mutate(data => {
        if (!data) return
        return {
          ...data,
          teacherPhoto,
        }
      }, false)
    },
    onError: e => {
      if (e instanceof BadRequest) {
        state.setStatus(e.errors.fields.teacherPhoto.toString() || t('global.error'))
      } else {
        state.setStatus(t('global.error'))
      }
    },
  }))

  const courseLogoPatcher = useActOnBlur<number | null>(state => ({
    action: async logo => {
      if (!courseId) throw new Error()
      const newData = await patchLightCourseOptions(courseId, { logo }, 'patchCourseLogo')
      await mutate(data => {
        if (!data) return
        return {
          ...data,
          logo: newData.logo,
        }
      }, false)
    },
    onError: e => {
      if (e instanceof BadRequest) {
        state.setStatus(e.errors.fields.logo.toString() || t('global.error'))
      } else {
        state.setStatus(t('global.error'))
      }
    },
  }))

  const courseCoverPatcher = useActOnBlur<number | null>(state => ({
    action: async image => {
      if (!courseId) throw new Error()
      const newData = await patchLightCourseOptions(courseId, { image }, 'patchCourseCover')
      await mutate(data => {
        if (!data) return
        return {
          ...data,
          image: newData.image,
        }
      }, false)
    },
    onError: e => {
      if (e instanceof BadRequest) {
        state.setStatus(e.errors.fields.image.toString() || t('global.error'))
      } else {
        state.setStatus(t('global.error'))
      }
    },
  }))

  const issueCertificateUponCompletionPatcher = useActOnBlur<boolean>(state => ({
    action: async value => {
      if (!courseId) throw new Error()
      const newData = await patchLightCourseOptions(
        courseId,
        { issueCertificateUponCompletion: value },
        'patchIssueCertificateUponCompletion',
      )
      await mutate(data => {
        if (!data) return
        return {
          ...data,
          issueCertificateUponCompletion: newData.issueCertificateUponCompletion,
        }
      }, false)
    },
    onError: e => {
      if (e instanceof BadRequest) {
        state.setStatus(
          e.errors.fields.issueCertificateUponCompletion.toString() || t('global.error'),
        )
      } else {
        state.setStatus(t('global.error'))
      }
    },
  }))

  const certificateLogoPatcher = useActOnBlur<number | null>(state => ({
    action: async value => {
      if (!courseId) throw new Error()
      const newData = await patchLightCourseOptions(
        courseId,
        { certificateLogo: value },
        'patchCertificateLogo',
      )
      await mutate(data => {
        if (!data) return
        return {
          ...data,
          certificateLogo: newData.certificateLogo,
        }
      }, false)
    },
    onError: e => {
      if (e instanceof BadRequest) {
        state.setStatus(e.errors.fields.certificateLogo.toString() || t('global.error'))
      } else {
        state.setStatus(t('global.error'))
      }
    },
  }))

  const signaturePatcher = useActOnBlur<number | null>(state => ({
    action: async value => {
      if (!courseId) throw new Error()
      const newData = await patchLightCourseOptions(
        courseId,
        { signature: value },
        'patchSignature',
      )
      await mutate(data => {
        if (!data) return
        return {
          ...data,
          signature: newData.signature,
        }
      }, false)
    },
    onError: e => {
      if (e instanceof BadRequest) {
        state.setStatus(e.errors.fields.signature.toString() || t('global.error'))
      } else {
        state.setStatus(t('global.error'))
      }
    },
  }))

  const codePatcher = useActOnBlur<string>(
    state => ({
      action: async value => {
        if (!courseId) throw new Error()
        const newData = await patchLightCourseOptions(
          courseId,
          { embeddedCode: value || null },
          'patchCode',
        )
        await mutate(data => {
          if (!data) return
          return {
            ...data,
            embeddedCode: newData.embeddedCode,
          }
        }, false)
      },
      onError: e => {
        if (e instanceof BadRequest) {
          state.setStatus(e.errors.fields.embeddedCode.toString() || t('global.error'))
        } else {
          state.setStatus(t('global.error'))
        }
      },
    }),
    !isCodeValid,
  )

  const domainPatcher = useActOnBlur<number>(state => ({
    action: async value => {
      if (!courseId) throw new Error()
      const newData = await patchLightCourseOptions(courseId, { domainId: value }, 'patchDomain')
      await mutate(data => {
        if (!data) return
        return {
          ...data,
          domainId: newData.domainId,
        }
      }, false)
    },
    onError: e => {
      if (e instanceof BadRequest) {
        state.setStatus(e.errors.fields.domainId.toString() || t('global.error'))
      } else {
        state.setStatus(t('global.error'))
      }
    },
  }))

  const pathPatcher = useActOnBlur<string>(state => ({
    action: async value => {
      if (!courseId) throw new Error()
      const newData = await patchLightCourseOptions(courseId, { path: value }, 'patchPath')
      await mutate(data => {
        if (!data) return
        return {
          ...data,
          path: newData.path,
        }
      }, false)
    },
    onError: e => {
      if (e instanceof BadRequest) {
        state.setStatus(e.errors.fields.path.toString() || t('global.error'))
      } else {
        state.setStatus(t('global.error'))
      }
    },
  }))

  const descriptionPatcher = useActOnBlur<string>(state => ({
    action: async value => {
      if (!courseId) throw new Error()
      const newData = await patchLightCourseOptions(
        courseId,
        { description: value },
        'patchDescription',
      )
      await mutate(data => {
        if (!data) return
        return {
          ...data,
          description: newData.description,
        }
      }, false)
    },
    onError: e => {
      if (e instanceof BadRequest) {
        state.setStatus(e.errors.fields.description.toString() || t('global.error'))
      } else {
        state.setStatus(t('global.error'))
      }
    },
  }))

  const namePatcher = useActOnBlur<string>(state => ({
    action: async value => {
      if (!courseId) throw new Error()
      const newData = await patchLightCourseOptions(courseId, { name: value }, 'patchName')
      await mutate(data => {
        if (!data) return
        return {
          ...data,
          name: newData.name,
        }
      }, false)
    },
    onError: e => {
      if (e instanceof BadRequest) {
        state.setStatus(e.errors.fields.name.toString() || t('global.error'))
      } else {
        state.setStatus(t('global.error'))
      }
    },
  }))

  const handleTeacherPhotoChange = async (file?: DataFileInterface) => {
    handleBlur.teacherPhoto(file ? file.id : null)
  }

  const handleCourseLogoChange = async (file?: DataFileInterface) => {
    handleBlur.logo(file ? file.id : null)
  }

  const handleCourseCoverChange = async (file?: DataFileInterface) => {
    handleBlur.image(file ? file.id : null)
  }

  const handleCertificateLogoChange = async (file?: DataFileInterface) => {
    handleBlur.certificateLogo(file ? file.id : null)
  }

  const handleSignatureChange = async (file?: DataFileInterface) => {
    handleBlur.signature(file ? file.id : null)
  }

  const handleDomainChange = async (domainId?: DomainId) => {
    domainConfirmationModal.open(domainId)
  }

  const handleDomainChangeConfirm = async () => {
    if (domainConfirmationModal.data) {
      await handleBlur.domainId(domainConfirmationModal.data)
    }
  }

  const handleIssueCertificateUponCompletionChange = async (value: boolean) => {
    handleBlur.issueCertificateUponCompletion(value)
  }

  const errors = {
    instructorName: instructorNamePatcher.error,
    name: namePatcher.error,
    description: descriptionPatcher.error,
    embeddedCode: codePatcher.error,
    path: pathPatcher.error,
    teacherPhoto: instructorPhotoPatcher.error,
    domainId: domainPatcher.error,
    logo: courseLogoPatcher.error,
    image: courseCoverPatcher.error,
    issueCertificateUponCompletion: issueCertificateUponCompletionPatcher.error,
    certificateLogo: certificateLogoPatcher.error,
    signature: signaturePatcher.error,
  }

  const isSuccess = {
    instructorName: instructorNamePatcher.isSuccess,
    name: namePatcher.isSuccess,
    description: descriptionPatcher.isSuccess,
    embeddedCode: codePatcher.isSuccess,
    path: pathPatcher.isSuccess,
    teacherPhoto: instructorPhotoPatcher.isSuccess,
    domainId: domainPatcher.isSuccess,
    logo: courseLogoPatcher.isSuccess,
    image: courseCoverPatcher.isSuccess,
    issueCertificateUponCompletion: issueCertificateUponCompletionPatcher.isSuccess,
    certificateLogo: certificateLogoPatcher.isSuccess,
    signature: signaturePatcher.isSuccess,
  }

  const statusIcon = {
    instructorName: instructorNamePatcher.statusIcon,
    name: namePatcher.statusIcon,
    description: descriptionPatcher.statusIcon,
    embeddedCode: codePatcher.statusIcon,
    path: pathPatcher.statusIcon,
    teacherPhoto: instructorPhotoPatcher.statusIcon,
    domainId: domainPatcher.statusIcon,
    logo: courseLogoPatcher.statusIcon,
    image: courseCoverPatcher.statusIcon,
    issueCertificateUponCompletion: issueCertificateUponCompletionPatcher.statusIcon,
    certificateLogo: certificateLogoPatcher.statusIcon,
    signature: signaturePatcher.statusIcon,
  }

  const handleBlur = {
    instructorName: instructorNamePatcher.handleBlur,
    name: namePatcher.handleBlur,
    description: descriptionPatcher.handleBlur,
    embeddedCode: codePatcher.handleBlur,
    path: pathPatcher.handleBlur,
    teacherPhoto: instructorPhotoPatcher.handleBlur,
    domainId: domainPatcher.handleBlur,
    logo: courseLogoPatcher.handleBlur,
    image: courseCoverPatcher.handleBlur,
    issueCertificateUponCompletion: issueCertificateUponCompletionPatcher.handleBlur,
    certificateLogo: certificateLogoPatcher.handleBlur,
    signature: signaturePatcher.handleBlur,
  }

  const isInProgress = {
    instructorName: instructorNamePatcher.isInProgress,
    name: namePatcher.isInProgress,
    description: descriptionPatcher.isInProgress,
    embeddedCode: codePatcher.isInProgress,
    path: pathPatcher.isInProgress,
    teacherPhoto: instructorPhotoPatcher.isInProgress,
    domainId: domainPatcher.isInProgress,
    logo: courseLogoPatcher.isInProgress,
    image: courseCoverPatcher.isInProgress,
    issueCertificateUponCompletion: issueCertificateUponCompletionPatcher.isInProgress,
    certificateLogo: certificateLogoPatcher.isInProgress,
    signature: signaturePatcher.isInProgress,
  }

  return {
    tempState: {
      ...tempState,
      teacherPhoto: courseData?.teacherPhoto,
      domainId: courseData?.domainId,
      logo: courseData?.logo,
      image: courseData?.image,
      issueCertificateUponCompletion: courseData?.issueCertificateUponCompletion,
      certificateLogo: courseData?.certificateLogo,
      signature: courseData?.signature,
    },
    setTempState,
    courseData,
    errors,
    isSuccess,
    statusIcon,
    handleBlur,
    isInProgress,

    handleTeacherPhotoChange,
    handleCourseLogoChange,
    handleCourseCoverChange,
    handleCertificateLogoChange,
    handleSignatureChange,
    handleIssueCertificateUponCompletionChange,

    domainConfirmationModal,
    handleDomainChange,
    handleDomainChangeConfirm,

    isCodeValid,
    setIsCodeValid,
  }
}
